import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { DeliveryNoteStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, UnitPrecisionSchema, } from './primitives.js';
import { ComponentTrackingResponseSchema, ComponentTrackingSchema, SalesHeaderInputFields, SalesHeaderPatchFields, SalesHeaderResponseFields, } from './sales-invoices.js';
const c = initContract();
/**
 * Delivery Notes (SAL-03) — "what actually left the warehouse", the moment
 * the goods stop being yours. No money on the document at all: a delivery
 * note counts things.
 *
 * Raised against a CONFIRMED sales order (BR-SAL-03-11: a real reference).
 * Partial deliveries are the central case; two notes never claim the same
 * units — the delivered quantity is capped cumulatively over POSTED notes
 * (BR-SAL-03-02).
 *
 * CONFIRMING is the posting step (BR-SAL-03-07, ruling R8):
 *   - one 'sale' movement per delivered quantity at the location's
 *     weighted-average cost (the writer refuses insufficient stock with
 *     BR-INV-04-08's sentence — ERR-SAL-03-07's shape);
 *   - the SAL-03 §12 entry: DR 5040 Cost of Sales / CR 1131 Inventory.
 *     COGS posts ON DELIVERY (the spec's proposed default) — an
 *     order-sourced invoice never posts it a second time;
 *   - the order's delivered % + partially_delivered/fulfilled transition
 *     (BR-SAL-03-04), and the reservation release (adjustReserved −).
 *
 * Permission area: sales (SAL-03 §9 leaves "who may confirm a delivery" to
 * PART 16; every sales document rides `sales.*`, confirming = approve).
 */
// ── The fixed sentences of SAL-03 ───────────────────────────────────────────
export const DELIVERY_MESSAGES = {
    /** BR-SAL-03-01 / ERR-SAL-03-01, quoted exactly. */
    nothingToDeliver: 'Type what is being delivered before confirming.',
    /** BR-SAL-03-02 / ERR-SAL-03-02, quoted exactly. */
    overOrdered: (qty, code, ordered) => `You cannot deliver ${qty} of ${code} against an order for ${ordered}.`,
    /** The cumulative half of BR-SAL-03-02 — earlier notes already took some. */
    overRemaining: (ordered, code, delivered, left) => `The order asked for ${ordered} of ${code} and ${delivered} have already gone. You can deliver at most ${left} more.`,
    /** SAL-03 §15 "Negative": goods coming back are a sales return. */
    negativeQty: 'A delivered quantity cannot be negative. Goods coming back are a sales return.',
    /** R8 / BR-SAL-03-07 — the confirmed lock. */
    postedLocked: 'This delivery is confirmed and locked. Goods coming back are a sales return.',
    /** Only a draft moves on. */
    notADraft: 'Only a draft delivery can change. This one has already moved on.',
    /** Deliveries need a commitment. */
    orderNotDeliverable: 'Only a confirmed order can be delivered. Confirm the order first.',
    /** BR-SAL-02-06's shape. */
    orderCancelled: (code) => `${code} was cancelled. Nothing can be delivered against it.`,
    /** Everything already went (the cap at zero remaining). */
    nothingLeft: (code) => `${code} is fully delivered. There is nothing left to send.`,
    /** A DN line must name an order line on ITS order. */
    lineNotOnOrder: (lineNo) => `Line ${lineNo} is not on the sales order this delivery is against.`,
    /** Only item-linked order lines physically ship. */
    freeTextNotDeliverable: (description) => `"${description}" is not a catalogue item. Only item lines can be delivered from stock.`,
    /** An item line without a warehouse cannot ship. */
    warehouseRequired: (description) => `Pick the warehouse ${description} leaves from.`,
    /** Only the posted state cancels (GEN-70; a draft deletes instead). */
    cancelOnlyPosted: 'Only a confirmed delivery can be cancelled. A draft is edited or deleted instead.',
    /** GEN-72: the cancelling act carries its reason. */
    cancelReasonRequired: 'A confirmed delivery can only be cancelled with a reason, which is written to the audit log.',
    /** Wave 5B — proof of delivery is recorded on a note that was, or will be, delivered; never on a cancelled one. */
    proofOnCancelled: 'This delivery was cancelled. Nothing was received, so there is nothing to sign for.',
    /** Wave 5B — the signature names an attachment that is not on this note. */
    signatureNotOnNote: 'The signature must be an attachment of this delivery note.',
    /** Wave 5B — an extra line (not on the order) must be a STOCK item: a service never leaves a shelf. */
    extraNotStockItem: (name) => `"${name}" is a service, not stock. Only stock items can be added as extra lines.`,
    /** Wave 5B — a note with no order behind it takes extra lines only. */
    noOrderForLine: 'This delivery has no sales order behind it. Only extra lines can be added to a direct delivery.',
    /** Wave 5B — a direct note needs a customer, an order-backed one takes the order's. */
    directNeedsCustomer: 'Pick the customer the goods go to, or the sales order they fulfil.',
    /**
     * Wave 5B — `sales.direct_delivery_enabled` is off. A zero-argument
     * sentence function so the structured twin below composes it the way
     * every other member of the family does.
     */
    directDeliveryDisabled: () => 'Direct deliveries are switched off for this company. Raise the delivery against a sales order, or turn on direct delivery in Settings.',
};
/**
 * D46 — the delivery family's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names an order, a quantity or
 * a line reaches an Arabic reader in Arabic. One builder per parameterised
 * member the API raises inside an HTTP exception body — the raise sites
 * throw THESE, and each `message` is composed by the `DELIVERY_MESSAGES`
 * twin so the two cannot drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `warehouseRequired` is raised by the INVOICE service, not the delivery
 * one — a direct item invoice moves stock itself, and a product line with no
 * warehouse cannot post there either; the family key still says what the
 * sentence is about. Every param is a display value — a formatted document
 * code, a decimal quantity string in the item's own unit precision, a line's
 * description, or a line NUMBER (a label, not a count: nothing pluralises on
 * it). No member carries a select token and none carries a count, so both
 * dictionaries interpolate every param verbatim.
 */
export const DELIVERY_REFUSALS = {
    overOrdered: (qty, code, ordered) => ({
        message: DELIVERY_MESSAGES.overOrdered(qty, code, ordered),
        key: 'dn.errOverOrdered',
        params: { qty, code, ordered },
    }),
    overRemaining: (ordered, code, delivered, left) => ({
        message: DELIVERY_MESSAGES.overRemaining(ordered, code, delivered, left),
        key: 'dn.errOverRemaining',
        params: { ordered, code, delivered, left },
    }),
    orderCancelled: (code) => ({
        message: DELIVERY_MESSAGES.orderCancelled(code),
        key: 'dn.errOrderCancelled',
        params: { code },
    }),
    nothingLeft: (code) => ({
        message: DELIVERY_MESSAGES.nothingLeft(code),
        key: 'dn.errNothingLeft',
        params: { code },
    }),
    lineNotOnOrder: (lineNo) => ({
        message: DELIVERY_MESSAGES.lineNotOnOrder(lineNo),
        key: 'dn.errLineNotOnOrder',
        params: { lineNo },
    }),
    freeTextNotDeliverable: (description) => ({
        message: DELIVERY_MESSAGES.freeTextNotDeliverable(description),
        key: 'dn.errFreeTextNotDeliverable',
        params: { description },
    }),
    warehouseRequired: (description) => ({
        message: DELIVERY_MESSAGES.warehouseRequired(description),
        key: 'dn.errWarehouseRequired',
        params: { description },
    }),
    /** Wave 5B — the item's name is a display value, interpolated verbatim. */
    extraNotStockItem: (name) => ({
        message: DELIVERY_MESSAGES.extraNotStockItem(name),
        key: 'dn.errExtraNotStockItem',
        params: { name },
    }),
    /** Wave 5B — no params: the key alone picks the dictionary sentence. */
    directDeliveryDisabled: () => ({
        message: DELIVERY_MESSAGES.directDeliveryDisabled(),
        key: 'dn.errDirectDeliveryDisabled',
        params: {},
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const DeliveryStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
/** Zero is legal per line — it marks the whole line backorder (§15). */
const DeliveredQtySchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Quantities are decimal strings with at most 3 decimals');
const LineTrackingInputFields = {
    /** BR-MD-03 tracking at document time: one serial per delivered unit. */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    /** R7: a KIT line's per-component capture (BR-MD-03-17). */
    components: ComponentTrackingSchema.array().max(200).optional(),
    note: z.string().max(500).optional(),
};
/** A line delivering an ORDER line — the central case, capped by BR-SAL-03-02. */
export const OrderDeliveryLineInputSchema = z
    .object({
    isExtra: z.literal(false).optional(),
    /** The order line being delivered, by its line number on the order. */
    soLineNo: z.number().int().positive(),
    qty: DeliveredQtySchema,
    /** Defaults to the order line's warehouse, else the note's header one. */
    warehouseId: z.uuid().optional(),
    ...LineTrackingInputFields,
})
    .strict();
/**
 * Wave 5B — an EXTRA line: goods leaving with the order that were never on
 * it (a sample, a replacement for a unit damaged in transit). Names a STOCK
 * item outright, carries its reason, and sits outside the order cap and the
 * delivered % — nothing was ordered, so nothing is capped or fulfilled. It
 * moves stock and posts COGS exactly like an order line; it is never
 * invoiced automatically. `unitId` is one of the item's sell units; the
 * quantity is stored in the stock unit.
 */
export const ExtraDeliveryLineInputSchema = z
    .object({
    isExtra: z.literal(true),
    itemId: z.uuid(),
    warehouseId: z.uuid().optional(),
    qty: DeliveredQtySchema,
    unitId: z.uuid().optional(),
    extraReason: z.string().trim().min(1).max(200),
    ...LineTrackingInputFields,
})
    .strict();
/** A line is one shape or the other; one note may carry both. */
export const DeliveryLineInputSchema = z.union([
    OrderDeliveryLineInputSchema,
    ExtraDeliveryLineInputSchema,
]);
export const DeliveryLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    /** Both null on an extra line (wave 5B) — there is no order line behind it. */
    soLineId: z.uuid().nullable(),
    soLineNo: z.number().int().nullable(),
    /** Wave 5B — not on the order; `extraReason` says why it went. */
    isExtra: z.boolean(),
    extraReason: z.string().nullable(),
    itemId: z.uuid(),
    itemCode: z.string(),
    description: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    qtyOrdered: z.string(),
    qty: z.string(),
    /**
     * BR-SAL-03-02's ledger, per line and server-computed: what EARLIER
     * posted notes already took of this order line (`deliveredBefore`) and
     * what was left for this note to send (`remaining` = ordered − before,
     * floored at zero). Both in the line's stock unit at 3 dp, like `qty`.
     * On a draft "before" is every posted note; on a posted note it is the
     * notes posted up to it, so a note read back later still shows the
     * figure it was confirmed against. The Backorder chip compares `qty`
     * against `remaining`, never against `qtyOrdered` (BR-SAL-03-05): the
     * second of two notes that together complete an order is not short.
     */
    deliveredBefore: z.string(),
    remaining: z.string(),
    /**
     * `N-4` — the unit BOTH figures on this row are in, and its decimals.
     *
     * `delivery_note_lines` has no `unit_id`: the quantity is the item's
     * STOCK unit, ordered and delivered alike, which is why one pair covers
     * both columns. (`BR-MD-03-04d`'s line-unit question is SAL-00 §5d's,
     * where `invoice_lines.unit_id` exists and a line can be typed in boxes.)
     *
     * Resolved on the server, like `InvoiceLineSchema`'s, so the printed note
     * and the screen cannot disagree about what "2" means.
     */
    unitSymbol: z.string(),
    unitPrecision: UnitPrecisionSchema,
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
    /** R7's sub-table as stored; empty for anything that is not a kit line. */
    components: ComponentTrackingResponseSchema.array(),
    note: z.string().nullable(),
    /** The amber row + Backorder chip: delivered < ordered (BR-SAL-03-05). */
    backorder: z.boolean(),
})
    .strict();
/**
 * Wave 5B — the logistics strip (SAL-03 §5a: who drove, in what, when it
 * left, where it went) and the proof-of-delivery block (§5d P1..P8: who
 * received, when, their note, the signature). Logistics are draft-side
 * content like every other header field; the proof is written AFTER
 * confirmation through the `proof` route, the one post-confirmation write
 * beside the notes (the R8 lock's carve-out).
 */
export const DeliveryLogisticsInputFields = {
    driver: z.string().trim().max(120).optional(),
    vehicleNo: z.string().trim().max(40).optional(),
    vehicleType: z.string().trim().max(40).optional(),
    departedAt: z.iso.datetime().optional(),
    /** Absent on create = prefilled from the customer branch's address, else the customer's shipping address. */
    deliveryAddress: z.string().trim().max(300).optional(),
};
export const DeliveryLogisticsPatchFields = {
    driver: z.string().trim().max(120).nullable().optional(),
    vehicleNo: z.string().trim().max(40).nullable().optional(),
    vehicleType: z.string().trim().max(40).nullable().optional(),
    departedAt: z.iso.datetime().nullable().optional(),
    deliveryAddress: z.string().trim().max(300).nullable().optional(),
};
export const DeliveryLogisticsResponseFields = {
    driver: z.string().nullable(),
    vehicleNo: z.string().nullable(),
    vehicleType: z.string().nullable(),
    departedAt: z.string().nullable(),
    deliveryAddress: z.string().nullable(),
    /** The proof block — null until somebody signs for the goods. */
    receivedBy: z.string().nullable(),
    receivedAt: z.string().nullable(),
    receiverNote: z.string().nullable(),
    /** An attachment of kind `delivery_note` on this note (the signed copy / pad capture). */
    signatureAttachmentId: z.uuid().nullable(),
};
/**
 * The proof route's body. `receivedBy` is the one thing a proof cannot do
 * without (BR-SAL-03-08's first clause: the recipient's name); the time
 * defaults to now on the server, the note and the signature are optional.
 */
export const DeliveryProofRequestSchema = z
    .object({
    receivedBy: z.string().trim().min(1).max(120),
    receivedAt: z.iso.datetime().optional(),
    receiverNote: z.string().trim().max(500).optional(),
    signatureAttachmentId: z.uuid().optional(),
})
    .strict();
export const DeliveryNoteSchema = z
    .object({
    id: z.uuid(),
    /** DN-#### — its own series. */
    code: z.string(),
    deliveryNo: z.number().int(),
    status: DeliveryStatusSchema,
    deliveryDate: IsoDateSchema,
    /** Both null on a DIRECT note (wave 5B) — raised to the customer with no order behind it. */
    salesOrderId: z.uuid().nullable(),
    salesOrderCode: z.string().nullable(),
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /**
     * Wave 5B — INV-F2…F6 on the note, defaulted from the order at creation
     * (project, customer branch, manual ref, book no, salesperson), with the
     * names the server resolved. The note's OWN warehouse pair follows and
     * overrides the fragment's nullable one: field 11 has been NOT NULL
     * since birth.
     */
    ...SalesHeaderResponseFields,
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    ...DeliveryLogisticsResponseFields,
    lines: DeliveryLineSchema.array(),
    /** Footer: QTY ORDERED / QTY DELIVERED (S1/S2 — S3's package count is S2, label corrected per BR-SAL-03-09). */
    totalOrdered: z.string(),
    totalDelivered: z.string(),
    /** The §12 cost that left with the goods (posted notes only; '0.00' while draft). */
    costTotal: z.string(),
    /**
     * OTHER draft notes against the same order, ids and DN codes in step.
     * `deliveredBySoLine` counts POSTED notes only (BR-SAL-03-02's ledger is
     * what left, not what somebody typed), so two drafts raised against one
     * order both default to the full remainder and the second can never
     * post. The editor warns off this list and, once the order is fully
     * delivered, offers to delete the leftovers through the ordinary delete
     * route. Empty on a note with no siblings.
     */
    pendingDraftIds: z.uuid().array(),
    pendingDraftCodes: z.string().array(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /** GEN-72's stamp trio — set once by cancel, null otherwise. */
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    cancellationJournalEntryId: z.uuid().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CancelDeliveryRequestSchema = z
    .object({
    /** GEN-72: cancelling must ask for a reason and store it. */
    reason: z.string().trim().min(1, DELIVERY_MESSAGES.cancelReasonRequired).max(500),
})
    .strict();
export const CreateDeliveryRequestSchema = z
    .object({
    /**
     * The order this note fulfils (BR-SAL-03-11). Absent = a DIRECT note
     * (wave 5B): `partnerId` names the customer instead, the note starts
     * with no lines and takes extra lines only, and the company's
     * `sales.direct_delivery_enabled` switch must be on.
     */
    salesOrderId: z.uuid().optional(),
    /** Required when `salesOrderId` is absent; ignored beside an order (the order's customer wins). */
    partnerId: z.uuid().optional(),
    deliveryDate: IsoDateSchema,
    /**
     * Wave 5B — the header fields, each OPTIONAL: an absent one is defaulted
     * from the order (its branch, project, refs and salesperson), a present
     * one is the clerk's own choice. The warehouse that follows overrides
     * the fragment's optional one: it is the origin default and required.
     */
    ...SalesHeaderInputFields,
    /** The origin default for lines that name none. */
    warehouseId: z.uuid(),
    ...DeliveryLogisticsInputFields,
    /** Absent = every item-linked order line at its remaining quantity (nothing on a direct note). */
    lines: DeliveryLineInputSchema.array().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((body) => body.salesOrderId !== undefined || body.partnerId !== undefined, {
    message: DELIVERY_MESSAGES.directNeedsCustomer,
    path: ['partnerId'],
});
export const UpdateDeliveryRequestSchema = z
    .object({
    deliveryDate: IsoDateSchema.optional(),
    /** Wave 5B — null clears a header field; the warehouse itself never clears. */
    ...SalesHeaderPatchFields,
    warehouseId: z.uuid().optional(),
    ...DeliveryLogisticsPatchFields,
    lines: DeliveryLineInputSchema.array().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
/**
 * Confirming carries at most one thing: WHY the shelf is being overridden.
 * A confirmation that takes a line below zero in a negative-allowed
 * warehouse needs `inventory.override_negative_stock`; the reason is the
 * confirming person's own words, written to the audit row of the post
 * (GEN-72's shape for a posting that changes what the books say is on the
 * shelf). Optional on the wire — a note that leaves nothing negative has
 * nothing to explain — and ignored by nothing: when present it is recorded
 * whatever the stock did.
 */
export const PostDeliveryRequestSchema = z
    .object({
    negativeStockReason: z.string().trim().min(1).max(500).optional(),
})
    .strict();
const deliveryParams = z.object({ deliveryId: z.uuid() }).strict();
export const deliveryNotesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/sales/deliveries',
        query: pagedQuery({
            salesOrderId: z.uuid().optional(),
            /** Wave 2 — the customer, for the list's filter and CRM-04's drill-through. */
            partnerId: z.uuid().optional(),
            status: DeliveryStatusSchema.optional(),
            /** GEN-50 / the header rail's Find: the printed number or the customer's name. */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the delivery date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('deliveries', DeliveryNoteSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Delivery notes (sales.view), PAGED (NFR-42).',
    },
    get: {
        method: 'GET',
        path: '/api/sales/deliveries/:deliveryId',
        pathParams: deliveryParams,
        responses: {
            200: DeliveryNoteSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One delivery note with its lines (sales.view).',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail (wave 2 of specs/erp-gap-analysis.md):
     * the delivery notes on either side of this one in number order (null at an
     * end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/sales/deliveries/:deliveryId/neighbours',
        pathParams: deliveryParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next delivery note by number (sales.view).',
    },
    /** S4 — the "Go to no." box: one delivery note by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/sales/deliveries/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One delivery note by its printed number (sales.view).',
    },
    create: {
        method: 'POST',
        path: '/api/sales/deliveries',
        body: CreateDeliveryRequestSchema,
        responses: {
            201: DeliveryNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft delivery against a confirmed order (sales.create). Lines default to the remaining quantities — the Load behavior; the cumulative BR-SAL-03-02 cap applies.',
    },
    update: {
        method: 'PATCH',
        path: '/api/sales/deliveries/:deliveryId',
        pathParams: deliveryParams,
        body: UpdateDeliveryRequestSchema,
        responses: {
            200: DeliveryNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a draft delivery (sales.edit); confirmed answers 409 (BR-SAL-03-07).',
    },
    proof: {
        method: 'PATCH',
        path: '/api/sales/deliveries/:deliveryId/proof',
        pathParams: deliveryParams,
        body: DeliveryProofRequestSchema,
        responses: {
            200: DeliveryNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Wave 5B — record the proof of delivery (sales.edit): who received the goods, when, their note and the signature attachment. Allowed on a draft and on a CONFIRMED note (the one post-confirmation write beside the notes); a cancelled note answers 409.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/sales/deliveries/:deliveryId',
        pathParams: deliveryParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a draft delivery (sales.delete); confirmed answers 409.',
    },
    post: {
        method: 'POST',
        path: '/api/sales/deliveries/:deliveryId/post',
        pathParams: deliveryParams,
        body: PostDeliveryRequestSchema.optional(),
        responses: {
            200: DeliveryNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Confirm delivery (sales.approve): sale movements at weighted-average cost, DR 5040 / CR 1131, order delivered % + status, reservations released. Once only. A line that would go below zero needs the warehouse to allow negative stock AND inventory.override_negative_stock (BR-INV-04-08); the optional negativeStockReason rides the audit row.',
    },
    cancel: {
        method: 'POST',
        path: '/api/sales/deliveries/:deliveryId/cancel',
        pathParams: deliveryParams,
        body: CancelDeliveryRequestSchema,
        responses: {
            200: DeliveryNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Cancel a CONFIRMED delivery (sales.approve — BR-SAL-03-07's tail): the goods return to stock at the same unit cost they left at, the COGS entry reversed on the delivery's own date (GEN-72), the order's delivered figures and reservations restored (BR-SAL-03-04 run backwards). Refused into a closed period (GEN-73/BR-SAL-03-10). Reason required and audited.",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/sales/deliveries/stats',
        responses: {
            200: DeliveryNoteStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'SAL-03 list tiles (sales.view): drafts to post, delivered this week, confirmed orders awaiting delivery, partially delivered orders — in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=delivery-notes.js.map